import { VdToastNotificationService } from '@vend/vend.ui'
import angular, { IScope } from 'angular'
import { ApiButtonLayout } from '@vend/types'

import { trackError } from '@vend/utilities'
import { STATES } from '../../../../lib/routes'
import { quickKeyLayoutsStore } from '../../factories/database/quick-key-layouts-store'
import { QuickKeysManager } from '../../managers/quick-keys-manager'
import { RegisterManager } from '../../managers/register-manager'
import { SyncManager } from '../../managers/sync-manager'
import { SaleManager } from '../../managers/sale-manager'
import {
  $state,
  $transitions,
  $stateParams,
  $log,
} from '../../ng-compat/imports'
import { DebounceOptions } from '../../constants'

/**
 * =======================================
 * EDIT QUICK KEY LAYOUT CONTROLLER
 * =======================================
 */
class EditQuickKeyLayoutCtrl {
  public isCloseFolderEnabled: $TSFixMe
  public isNewLayout: $TSFixMe
  public isSaving = false
  public layoutDataAdaptor: $TSFixMe
  public quickKeyLayout: $TSFixMe
  public quickKeyLayoutName: $TSFixMe
  public quickKeySettingsForm: $TSFixMe
  public settingsState = STATES.SETTINGS
  public suggestionSource: $TSFixMe
  public debounceOptions: DebounceOptions | undefined

  constructor(
    private $scope: IScope,
    private quickKeysManager: QuickKeysManager,
    private syncManager: SyncManager,
    private saleManager: SaleManager,
    private vdToastNotificationService: VdToastNotificationService,
    private registerManager: RegisterManager,
    private productModals: $TSFixMe,
    private productSuggestions: $TSFixMe
  ) {
    'ngInject'
    this.suggestionSource = this.productSuggestions.getSource()
    this.debounceOptions = this.saleManager.debounceOptions
    const unbindStateChangeListener = $transitions.onStart({}, () =>
      this.syncManager.sync()
    )
    this.$scope.$on('$destroy', () => {
      if (this.layoutDataAdaptor) {
        this.layoutDataAdaptor.off('layoutUpdate', this._onLayoutUpdate)
        this.layoutDataAdaptor.off(
          'layoutUpdateError',
          this._onLayoutUpdateError
        )
      }
      this.registerManager.off(
        'activeRegisterChange',
        this._onActiveRegisterChange
      )
      unbindStateChangeListener()
    })
    this.quickKeysManager
      .init()
      .then(() => quickKeyLayoutsStore.get($stateParams.layoutId))
      .then((quickKeyLayout: ApiButtonLayout | undefined) => {
        if (!quickKeyLayout) {
          this._notifyLayoutNotFound()
          return
        }
        this.quickKeyLayout = quickKeyLayout
        this.isNewLayout = $stateParams.newLayout
        this.quickKeyLayoutName = this.isNewLayout
          ? ''
          : this.quickKeyLayout.name
        this.layoutDataAdaptor = this.quickKeysManager.createLayoutDataAdaptor()
        this.layoutDataAdaptor.whenRegistered().then(() => {
          this.layoutDataAdaptor.on('layoutUpdate', this._onLayoutUpdate, this)
          this.layoutDataAdaptor.on(
            'layoutUpdateError',
            this._onLayoutUpdateError,
            this
          )
          this.registerManager.on(
            'activeRegisterChange',
            this._onActiveRegisterChange,
            this
          )
          this.isCloseFolderEnabled = !this.layoutDataAdaptor.getCloseFolderOption()
        })
      })
  }

  /**
   * Handles the selection of a product from the product search.
   *
   * @method onSelectResult
   *
   * @param  result {ProductResult|Suggestion}
   *         The selected product search result
   */
  public onSelectResult(productResult: $TSFixMe) {
    // @todo is this even possible?
    if (angular.isArray(productResult)) {
      if (productResult.length > 1) {
        $log.warn(
          '[Edit Quick Key Layout Search] Multiple product matches',
          productResult
        )
      }
      productResult = productResult[0]
    }
    $log.debug('[Edit Quick Key Layout Search] Product Selected', productResult)
    if (productResult.variantCount && !productResult.exactMatch) {
      const options = {
        parentProduct: productResult.product,
        title: 'What would you like to set as this Quick Key?',
        allowVariantParentSelect: true,
      }
      this.productModals
        .selectVariant(options)
        .then((productResult: $TSFixMe) => {
          if (productResult.exactMatch) {
            this.layoutDataAdaptor.addProduct(productResult.variant, {
              exactMatch: productResult.exactMatch,
            })
          } else {
            this.layoutDataAdaptor.addProduct(productResult.product, {
              exactMatch: productResult.exactMatch,
            })
          }
        })
        .catch(() => {}) // Empty catch here to avoid raygun unhandled rejection error
    } else {
      this.layoutDataAdaptor.addProduct(productResult.product, {
        exactMatch: productResult.exactMatch,
      })
    }
  }

  /**
   * Determine if the quick key layout can be edited.
   *
   * @method canUpdateLayout
   *
   * @return {Boolean} True if the quick key layout can be edited, false otherwise
   */
  public canUpdateLayout() {
    return !this.isSaving
  }

  /**
   * Save the quick key settings form
   *
   * @method save
   */
  public save() {
    this.isSaving = true
    if (this.quickKeySettingsForm.$invalid) {
      this.isSaving = false
      this.quickKeySettingsForm.layoutName.$setDirty()
      this.vdToastNotificationService.negative({
        message: 'Please complete the highlighted fields to continue.',
      })
      return
    }
    if (!this.quickKeySettingsForm.$dirty) {
      this.isSaving = false
      return $state.go(this.settingsState)
    }
    this.layoutDataAdaptor
      .setCloseFolderOption(!this.isCloseFolderEnabled)
      .then(() => this._updateLayoutName())
      .then(() => this.quickKeySettingsForm.$setPristine())
      .catch(() => {
        this.vdToastNotificationService.negative({
          message: 'There was a problem updating the layout, please try again.',
        })
      })
      .finally(() => {
        this.isSaving = false
        this.isNewLayout = false
      })
  }

  // ============================== Private API ======================== //
  /**
   * Updates the name of the layout
   *
   * @private
   *
   * @method _updateLayoutName
   *
   * @return {Promise} A promise for updating the layout name
   */
  private _updateLayoutName() {
    return this.quickKeysManager
      .updateLayout(this.quickKeyLayout.id, { name: this.quickKeyLayoutName })
      .then(() => {
        this.quickKeyLayout.name = this.quickKeyLayoutName
      })
  }

  /**
   * Get the specified layout from the quickKeyLayoutsStore and set it on the controller.
   *
   * @private
   * @method _setQuickKeyLayout
   */
  private _setQuickKeyLayout() {
    return quickKeyLayoutsStore
      .get($stateParams.layoutId)
      .then(quickKeyLayout => {
        this.quickKeyLayout = quickKeyLayout
      })
  }

  /**
   * Method for handling layoutUpdate event
   *
   * @private
   *
   * @method _onLayoutUpdate
   *
   * @param event
   */
  private _onLayoutUpdate(event: $TSFixMe) {
    if (event && event.layoutData) {
      quickKeyLayoutsStore.put(event.layoutData)
    }
  }

  /**
   * Method for handling the layoutUpdateError event
   *
   * @private
   *
   * @method _onLayoutUpdateError
   *
   * @param event
   */
  private _onLayoutUpdateError(event: $TSFixMe) {
    $log.warn(
      '[Edit Quick Key Layout] Error updating quick key layout',
      event.error
    )
    this.vdToastNotificationService.negative({
      message:
        'There was an error making that change, please wait while we refresh your quick keys.',
    })
    // Clear the current layout so we revert to rendering a spinner until the sync has complete
    this.quickKeyLayout = null
    this.syncManager.sync().then(() => this._setQuickKeyLayout())
  }

  /**
   * When the active register changes, switch back to the root settings state.
   *
   * @private
   * @method _onActiveRegisterChange
   */
  private _onActiveRegisterChange() {
    $state.go(this.settingsState)
  }

  /**
   * Notifies the user that the quick key layout (obtained via stateParams) could not be found. Switch back to the
   * root settings state.
   *
   * @private
   * @method _notifyLayoutNotFound
   */
  private _notifyLayoutNotFound() {
    this.vdToastNotificationService.negative({
      message:
        'There was a problem loading the quick key layout. Please try again.',
    })
    trackError(
      '[EditQuickKeyLayoutCtrl] Error attempting to edit unknown layout',
      {
        data: { layoutId: $stateParams.layoutId },
      }
    )
    $state.go(STATES.SETTINGS)
  }
}

/**
 * Controller for editing a quick key layout
 *
 * @module webregister.controllers.EditQuickKeyLayoutCtrl
 */
angular
  .module('webregister.controllers')
  .controller('EditQuickKeyLayoutCtrl', EditQuickKeyLayoutCtrl)
